import { useEffect, useRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MarkdownRenderer } from './MarkdownRenderer';

interface MarkdownEditorProps {
  value: string;
  onChange: (v: string) => void;
  onWikilinkClick?: (target: string) => void;
  className?: string;
}

/**
 * Editor puro markdown com preview lado-a-lado.
 * Sem rich text — respeita o markdown original do vault.
 */
export function MarkdownEditor({
  value,
  onChange,
  onWikilinkClick,
  className,
}: MarkdownEditorProps): JSX.Element {
  const [showPreview, setShowPreview] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Atalhos básicos: Tab insere 2 espaços em vez de mudar foco
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const textarea = el;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Tab') {
        e.preventDefault();
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue = value.slice(0, start) + '  ' + value.slice(end);
        onChange(newValue);
        requestAnimationFrame(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 2;
        });
      }
    }
    textarea.addEventListener('keydown', onKey);
    return () => textarea.removeEventListener('keydown', onKey);
  }, [value, onChange]);

  return (
    <div className={cn('flex flex-col h-full min-h-0', className)}>
      <div className="flex items-center justify-end px-2 py-1 border-b border-border bg-muted/30">
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          title={showPreview ? 'Ocultar preview' : 'Mostrar preview'}
        >
          {showPreview ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {showPreview ? 'Ocultar' : 'Preview'}
        </button>
      </div>

      <div className={cn('flex-1 min-h-0 grid gap-0', showPreview ? 'grid-cols-2' : 'grid-cols-1')}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          className={cn(
            'w-full h-full resize-none p-4 bg-background font-mono text-sm',
            'focus:outline-none border-0',
            showPreview && 'border-r border-border',
          )}
          placeholder="Digite em markdown…"
        />
        {showPreview && (
          <div className="overflow-y-auto p-4 bg-card/30">
            {value.trim() === '' ? (
              <p className="text-sm text-muted-foreground">Preview aparece aqui conforme você digita.</p>
            ) : (
              <MarkdownRenderer content={value} onWikilinkClick={onWikilinkClick} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
